import { useState } from "react";

/**
 * FrontierScene — procedural LAW DOGG backdrop. A lone gunslinger against a
 * Tombstone dusk: blazing low sun, false-front town skyline, saguaro, telegraph
 * pole, drifting dust. Pure SVG/CSS, no assets, palette baked frontier-warm.
 *
 * Photo override: if a file exists at /bg-frontier/<variant>.webp it fades in
 * on top of the procedural scene (drop AI-generated Doc Holliday / Wyatt Earp
 * art there and it takes over automatically). On 404 the <img> never loads and
 * the procedural scene shows through.
 */
export default function FrontierScene({ variant }: { variant: string }) {
  const [photoLoaded, setPhotoLoaded] = useState(false);
  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
      <svg
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 h-full w-full"
      >
        <defs>
          <linearGradient id="ld-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0c0807" />
            <stop offset="36%" stopColor="#211310" />
            <stop offset="60%" stopColor="#4f250f" />
            <stop offset="79%" stopColor="#9a4213" />
            <stop offset="100%" stopColor="#d67e27" />
          </linearGradient>
          <radialGradient id="ld-sun" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffe8b6" />
            <stop offset="26%" stopColor="#ffb44e" />
            <stop offset="60%" stopColor="#c85d18" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#c85d18" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="ld-ground" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1b0f08" />
            <stop offset="100%" stopColor="#060302" />
          </linearGradient>
          <radialGradient id="ld-vig" cx="50%" cy="44%" r="74%">
            <stop offset="0%" stopColor="#000" stopOpacity="0" />
            <stop offset="58%" stopColor="#000" stopOpacity="0" />
            <stop offset="100%" stopColor="#000" stopOpacity="0.74" />
          </radialGradient>
          <filter id="ld-soft"><feGaussianBlur stdDeviation="7" /></filter>
          <filter id="ld-softer"><feGaussianBlur stdDeviation="22" /></filter>
        </defs>

        <rect width="1600" height="900" fill="url(#ld-sky)" />
        <g fill="#f4e3c4">
          <circle cx="220" cy="120" r="1.6" opacity="0.6" />
          <circle cx="430" cy="80" r="1.1" opacity="0.45" />
          <circle cx="700" cy="150" r="1.2" opacity="0.5" />
          <circle cx="1180" cy="110" r="1.4" opacity="0.55" />
          <circle cx="1380" cy="180" r="1.1" opacity="0.4" />
          <circle cx="980" cy="78" r="1.0" opacity="0.4" />
        </g>

        <g className="ld-sun">
          <circle cx="840" cy="566" r="440" fill="url(#ld-sun)" />
          <circle cx="840" cy="566" r="118" fill="#ffeabf" opacity="0.92" filter="url(#ld-soft)" />
        </g>
        <rect x="0" y="556" width="1600" height="10" fill="#ffd28a" opacity="0.30" filter="url(#ld-soft)" />

        <g fill="#3a2014" opacity="0.9">
          <path d="M60 558 L60 506 L230 506 L230 558 Z" />
          <path d="M250 558 L250 524 L360 524 L372 506 L384 524 L384 558 Z" />
          <path d="M1180 558 L1180 514 L1320 514 L1320 558 Z" />
          <path d="M1340 558 L1340 528 L1470 528 L1470 558 Z" />
        </g>
        <g fill="#22120b">
          <path d="M0 600 L210 600 L226 552 L470 552 L486 600 L1010 600 L1030 560 L1180 560 L1196 600 L1600 600 L1600 640 L0 640 Z" />
        </g>
        <rect x="0" y="548" width="1600" height="64" fill="#e07a2a" opacity="0.08" filter="url(#ld-softer)" />

        <path d="M0 600 L1600 600 L1600 900 L0 900 Z" fill="url(#ld-ground)" />
        <ellipse cx="820" cy="744" rx="150" ry="16" fill="#000" opacity="0.5" filter="url(#ld-soft)" />

        <g stroke="#0f0a06" strokeWidth="6" fill="none">
          <path d="M360 620 L360 470" /><path d="M334 500 L386 500" /><path d="M338 516 L382 516" />
        </g>

        <g fill="#0f0a06">
          <path d="M150 760 L150 600 Q150 588 162 588 Q174 588 174 600 L174 760 Z" />
          <path d="M150 660 Q120 660 120 630 L120 600 Q120 592 128 592 Q136 592 136 600 L136 638 Q136 648 150 648 Z" />
          <path d="M174 678 Q204 678 204 648 L204 612 Q204 604 212 604 Q220 604 220 612 L220 656 Q220 666 174 666 Z" />
        </g>
        <g fill="#0c0805">
          <path d="M1452 770 L1452 642 Q1452 632 1462 632 Q1472 632 1472 642 L1472 770 Z" />
          <path d="M1472 692 Q1498 692 1498 668 L1498 646 Q1498 639 1505 639 Q1512 639 1512 646 L1512 674 Q1512 682 1472 682 Z" />
        </g>

        <g>
          <g fill="#e58a36" opacity="0.5" filter="url(#ld-soft)">
            <ellipse cx="820" cy="556" rx="50" ry="12" />
            <rect x="792" y="600" width="58" height="150" rx="20" />
          </g>
          <g fill="#070402">
            <path d="M793 590 Q788 600 786 628 L772 744 L806 744 L812 692 L820 744 L854 744 L840 628 Q838 600 833 590 Z" />
            <path d="M790 592 Q813 575 836 592 L834 615 Q813 602 792 615 Z" />
            <ellipse cx="813" cy="567" rx="14" ry="17" />
            <path d="M797 552 Q800 531 813 531 Q826 531 829 552 Z" />
            <ellipse cx="813" cy="553" rx="41" ry="9" />
            <path d="M836 612 L849 670 L839 674 L828 624 Z" />
            <ellipse cx="846" cy="680" rx="10" ry="14" />
          </g>
          <path d="M833 590 Q838 600 840 628 L851 702" stroke="#f0a44e" strokeWidth="2.4" fill="none" opacity="0.85" />
          <path d="M829 552 Q826 533 815 531" stroke="#f0a44e" strokeWidth="1.6" fill="none" opacity="0.7" />
        </g>

        <g className="ld-dust" fill="#f0b878">
          <circle cx="520" cy="690" r="2.2" opacity="0.5" />
          <circle cx="600" cy="730" r="1.6" opacity="0.4" />
          <circle cx="1080" cy="700" r="2.0" opacity="0.45" />
          <circle cx="1180" cy="740" r="1.5" opacity="0.35" />
          <circle cx="980" cy="760" r="1.8" opacity="0.4" />
          <circle cx="430" cy="752" r="1.4" opacity="0.3" />
        </g>

        <rect width="1600" height="900" fill="url(#ld-vig)" />
      </svg>

      <img
        src={`/bg-frontier/${variant}.webp`}
        alt=""
        aria-hidden
        onLoad={() => setPhotoLoaded(true)}
        draggable={false}
        className={`absolute inset-0 h-full w-full select-none object-cover transition-opacity duration-500 motion-safe:animate-kenburns ${
          photoLoaded ? "opacity-100" : "opacity-0"
        }`}
      />

      {photoLoaded ? (
        <img
          src={`/bg-frontier/${variant}.webp`}
          alt=""
          aria-hidden
          draggable={false}
          className="ld-breath pointer-events-none absolute inset-0 h-full w-full select-none object-cover mix-blend-screen motion-safe:animate-kenburns"
          style={{ filter: "brightness(1.5) contrast(1.35) saturate(1.25)" }}
        />
      ) : null}
    </div>
  );
}
